import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Slider, IconButton, Chip, Stack,
  CircularProgress, Divider, Tooltip, Button, Drawer, List, ListItemButton,
  ListItemText, Checkbox, Menu, MenuItem, TextField,
} from '@mui/material';
import {
  PlayArrow, Pause, SkipPrevious, SkipNext, CenterFocusStrong,
  Tune, Save, Close, ZoomIn, ZoomOut,
} from '@mui/icons-material';
import {
  flowService, type FlowPeriod, type FlowNode, type FlowView, type AccountFlowDetail,
} from '../services/flow-service';
import { FlowCanvas } from '../components/flow/FlowCanvas';
import { baseMoney, useBaseCurrency } from '../stores/currency-store';
import {
  type Point, type Viewport, layoutColumns, fitToNodes, typeColorVar, frameAt,
} from '../components/flow/useFlowGraph';

// Money moving between accounts of possibly different currencies: base currency.
const fmt = (n: number | string) => baseMoney(n, 0);

// Playback advances the fractional position a little each tick rather than
// jumping a whole month, so the graph glides instead of cutting.
const PLAY_TICK_MS = 40;
const PLAY_STEP = 0.022;

/** Swatches offered for per-account colour overrides. */
const SWATCHES = [
  '#2dd4a7', '#f0785e', '#8b7cff', '#ffbd66',
  '#4cc9f0', '#ff6b86', '#a3e635', '#f472b6',
];

export const FlowPage: React.FC = () => {
  // Subscribes the page so fmt() relabels when the base currency changes.
  useBaseCurrency();
  const [periods, setPeriods] = useState<FlowPeriod[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Fractional timeline position: 2.4 means 40% from period 2 toward 3.
  const [pos, setPos] = useState(0);
  const [playing, setPlaying] = useState(false);

  const [viewport, setViewport] = useState<Viewport>({ x: 0, y: 0, k: 1 });
  const [positions, setPositions] = useState<Record<string, Point>>({});
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [colorOverrides, setColorOverrides] = useState<Record<string, string>>({});

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AccountFlowDetail | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const [views, setViews] = useState<FlowView[]>([]);
  const [viewMenu, setViewMenu] = useState<null | HTMLElement>(null);
  const [saveName, setSaveName] = useState('');
  const [periodSeek, setPeriodSeek] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    flowService.getFlow()
      .then((r) => !cancelled && setPeriods(r.periods ?? []))
      .catch((e) => !cancelled && setError(e?.message ?? 'Failed to load flow'));
    flowService.listViews()
      .then((v) => {
        if (cancelled) return;
        setViews(v);
        // Reason: applying the default view here (before the canvas computes a
        // layout) is what makes a saved arrangement actually stick. The canvas
        // only auto-lays-out while `positions` is empty, so seeding it now
        // suppresses the default layout rather than fighting it.
        const def = v.find((x) => x.is_default);
        if (def) applyView(def, false);
      })
      .catch(() => { });
    return () => { cancelled = true; };
  }, []);

  // Union of nodes across every period keeps layout stable while scrubbing:
  // laying out per-period would make nodes jump as accounts appear and vanish.
  const allNodes = useMemo(() => {
    const m = new Map<string, FlowNode>();
    for (const p of periods ?? []) {
      for (const n of p.nodes) {
        const prev = m.get(n.account_id);
        if (!prev || Number(n.volume) > Number(prev.volume)) m.set(n.account_id, n);
      }
    }
    return [...m.values()];
  }, [periods]);

  useEffect(() => {
    if (!periods?.length) return;
    if (periodSeek) {
      const i = periods.findIndex((p) => p.period.slice(0, 10) === periodSeek);
      setPeriodSeek(null);
      if (i >= 0) { setPos(i); return; }
    }
    setPos(periods.length - 1);
  }, [periods, periodSeek]);

  useEffect(() => {
    if (!playing || !periods?.length) return;
    const last = periods.length - 1;
    timer.current = window.setInterval(() => {
      setPos((p) => (p >= last ? 0 : Math.min(last, p + PLAY_STEP)));
    }, PLAY_TICK_MS);
    return () => window.clearInterval(timer.current);
  }, [playing, periods?.length]);

  // The rendered frame is a blend of the two neighbouring months; its `period`
  // and `label` still point at a real month so drill-down never queries a
  // fabricated date.
  const period = useMemo(
    () => (periods?.length ? frameAt(periods, pos) : undefined),
    [periods, pos],
  );

  // Load drill-down detail whenever selection or period changes.
  useEffect(() => {
    if (!selectedId || !period) { setDetail(null); return; }
    const from = period.period.slice(0, 10);
    const d = new Date(from);
    const to = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
    let cancelled = false;
    flowService.getAccountDetail(selectedId, from, to)
      .then((r) => !cancelled && setDetail(r))
      .catch(() => !cancelled && setDetail(null));
    return () => { cancelled = true; };
  }, [selectedId, period]);

  const handleSelect = useCallback((id: string | null) => {
    setSelectedId(id);
    if (id) setPanelOpen(true);
  }, []);

  const resetLayout = useCallback(() => {
    const w = 1200, h = 560;
    const laid = layoutColumns(allNodes, { width: w, height: h });
    setPositions(laid);
    setViewport(fitToNodes(laid, w, h));
  }, [allNodes]);

  const toggleHidden = (id: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const saveView = async (name: string, isDefault = false) => {
    if (!name.trim()) return;
    const saved = await flowService.saveView({
      name: name.trim(),
      layout_mode: 'column',
      node_positions: positions,
      hidden_accounts: [...hidden],
      color_overrides: colorOverrides,
      viewport,
      active_period: period?.period?.slice(0, 10) ?? null,
      is_default: isDefault,
    });
    setViews((prev) => [saved, ...prev.filter((v) => v.id !== saved.id && v.name !== saved.name)]);
    setSaveName('');
  };

  /**
   * Apply a saved view's arrangement.
   *
   * `seekPeriod` is false on first load because `periods` may not have arrived
   * yet; the timeline defaults to the latest month in that case.
   */
  const applyView = useCallback((v: FlowView, seekPeriod = true) => {
    if (v.node_positions && Object.keys(v.node_positions).length) {
      setPositions(v.node_positions);
    }
    setHidden(new Set(v.hidden_accounts ?? []));
    setColorOverrides(v.color_overrides ?? {});
    if (v.viewport) setViewport(v.viewport);
    if (seekPeriod && v.active_period) {
      setPeriodSeek(v.active_period.slice(0, 10));
    }
  }, []);

  const loadView = (v: FlowView) => {
    applyView(v);
    setViewMenu(null);
  };

  if (error) return <Typography color="error" sx={{ p: 3 }}>{error}</Typography>;
  if (!periods || !period) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 400, gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          {periods ? 'No ledger activity to map yet' : 'Tracing your money…'}
        </Typography>
      </Box>
    );
  }

  const net = Number(period.total_in) - Number(period.total_out);

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="overline" color="text.secondary">Money map</Typography>
          <Typography variant="h3">Flow</Typography>
          <Typography variant="body2" color="text.secondary">
            Every dollar in, out, and between your accounts
          </Typography>
        </Box>
        <Stack direction="row" spacing={2.5} sx={{ textAlign: 'right' }}>
          <Stat label="In" value={fmt(period.total_in)} varName="--inflow-text" />
          <Stat label="Out" value={fmt(period.total_out)} varName="--outflow-text" />
          <Stat
            label="Net"
            value={`${net >= 0 ? '+' : ''}${fmt(net)}`}
            varName={net >= 0 ? '--inflow-text' : '--outflow-text'}
          />
        </Stack>
      </Stack>

      <Card>
        <CardContent sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h5" className="fw-num">{period.label}</Typography>
              <Chip label={`${period.edges.length} flows`} size="small" />
              <Chip label={`${period.nodes.length - hidden.size} accounts`} size="small" />
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Zoom out">
                <IconButton size="small" onClick={() => setViewport((v) => ({ ...v, k: Math.max(0.25, v.k / 1.2) }))}>
                  <ZoomOut fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom in">
                <IconButton size="small" onClick={() => setViewport((v) => ({ ...v, k: Math.min(4, v.k * 1.2) }))}>
                  <ZoomIn fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset layout & fit">
                <IconButton size="small" onClick={resetLayout}>
                  <CenterFocusStrong fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Accounts & colours">
                <IconButton size="small" onClick={() => setPanelOpen(true)}>
                  <Tune fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button size="small" startIcon={<Save />} onClick={(e) => setViewMenu(e.currentTarget)}>
                Views
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ border: '1px solid var(--border-soft)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
            <FlowCanvas
              period={period}
              allNodes={allNodes}
              viewport={viewport}
              onViewportChange={setViewport}
              positions={positions}
              onPositionsChange={setPositions}
              hidden={hidden}
              colorOverrides={colorOverrides}
              selectedId={selectedId}
              onSelect={handleSelect}
            />
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Drag nodes to rearrange · scroll to zoom · drag background to pan · click an account to drill in
          </Typography>

          <Divider sx={{ my: 1 }} />

          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton size="small" onClick={() => { setPlaying(false); setPos((p: number) => Math.max(0, Math.round(p) - 1)); }} aria-label="Previous month">
              <SkipPrevious fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setPlaying((p) => !p)}
              aria-label={playing ? 'Pause timeline' : 'Play timeline'}
              sx={{ background: 'var(--accent-soft)', color: 'var(--accent-text)' }}
            >
              {playing ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={() => { setPlaying(false); setPos((p: number) => Math.min(periods.length - 1, Math.round(p) + 1)); }} aria-label="Next month">
              <SkipNext fontSize="small" />
            </IconButton>
            <Slider
              size="small"
              min={0}
              max={Math.max(0, periods.length - 1)}
              step={0.01}
              value={pos}
              onChange={(_, v) => { setPlaying(false); setPos(v as number); }}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => periods[Math.round(v)]?.label ?? ''}
              aria-label="Timeline"
              sx={{ mx: 1.5 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70, textAlign: 'right' }}>
              {Math.round(pos) + 1} / {periods.length}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Menu anchorEl={viewMenu} open={!!viewMenu} onClose={() => setViewMenu(null)}>
        <Box sx={{ px: 2, py: 1, minWidth: 260 }}>
          <Typography variant="overline" color="text.secondary">Save current view</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            <TextField
              size="small" fullWidth placeholder="View name"
              value={saveName} onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { saveView(saveName); setViewMenu(null); } }}
            />
            <Button size="small" variant="contained" onClick={() => { saveView(saveName); setViewMenu(null); }}>
              Save
            </Button>
          </Stack>
        </Box>
        <Divider sx={{ my: 1 }} />
        {views.length === 0 && (
          <MenuItem disabled><ListItemText secondary="No saved views yet" /></MenuItem>
        )}
        {views.map((v) => (
          <MenuItem key={v.id} onClick={() => loadView(v)}>
            <ListItemText primary={v.name} secondary={v.is_default ? 'Default' : undefined} />
          </MenuItem>
        ))}
      </Menu>

      <Drawer anchor="right" open={panelOpen} onClose={() => setPanelOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h5">{detail ? detail.name : 'Accounts'}</Typography>
          <IconButton size="small" onClick={() => setPanelOpen(false)}><Close fontSize="small" /></IconButton>
        </Stack>

        {detail ? (
          <>
            <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
              <Stat label="In" value={fmt(detail.inflow)} varName="--inflow-text" />
              <Stat label="Out" value={fmt(detail.outflow)} varName="--outflow-text" />
              <Stat label="Balance" value={fmt(detail.balance)} varName="--text" />
            </Stack>
            <Typography variant="overline" color="text.secondary">Colour</Typography>
            <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
              {SWATCHES.map((c) => (
                <Box
                  key={c}
                  onClick={() => setColorOverrides((p) => ({ ...p, [detail.account_id]: c }))}
                  sx={{
                    width: 22, height: 22, borderRadius: '50%', background: c, cursor: 'pointer',
                    border: colorOverrides[detail.account_id] === c
                      ? '2px solid var(--text)' : '1px solid var(--border)',
                  }}
                />
              ))}
              <Button size="small" onClick={() => setColorOverrides((p) => {
                const n = { ...p }; delete n[detail.account_id]; return n;
              })}>Reset</Button>
            </Stack>
            <Typography variant="overline" color="text.secondary">
              Activity — {period.label}
            </Typography>
            <List dense sx={{ maxHeight: '55vh', overflow: 'auto' }}>
              {detail.entries.map((e) => (
                <ListItemButton key={e.entry_id + e.amount} sx={{ borderRadius: 1 }}>
                  <ListItemText
                    primary={e.description || e.counterparty || '—'}
                    secondary={`${e.date.slice(0, 10)} · ${e.counterparty}`}
                  />
                  <Typography
                    className="fw-num" variant="body2"
                    sx={{ color: Number(e.amount) >= 0 ? 'var(--inflow-text)' : 'var(--outflow-text)' }}
                  >
                    {Number(e.amount) >= 0 ? '+' : ''}{fmt(e.amount)}
                  </Typography>
                </ListItemButton>
              ))}
              {detail.entries.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                  No activity this period
                </Typography>
              )}
            </List>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Toggle accounts to hide them from the map.
            </Typography>
            <List dense sx={{ maxHeight: '70vh', overflow: 'auto' }}>
              {allNodes.map((n) => (
                <ListItemButton key={n.account_id} onClick={() => toggleHidden(n.account_id)} sx={{ borderRadius: 1 }}>
                  <Checkbox edge="start" size="small" checked={!hidden.has(n.account_id)} tabIndex={-1} disableRipple />
                  <Box sx={{
                    width: 10, height: 10, borderRadius: '50%', mr: 1,
                    background: colorOverrides[n.account_id] ?? `var(${typeColorVar(n.account_type)})`,
                  }} />
                  <ListItemText primary={n.name} secondary={n.account_type} />
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </Drawer>
    </Box>
  );
};

const Stat: React.FC<{ label: string; value: string; varName: string }> = ({ label, value, varName }) => (
  <Box>
    <Typography variant="overline" color="text.secondary">{label}</Typography>
    <Typography className="fw-num" variant="h6" sx={{ color: `var(${varName})` }}>{value}</Typography>
  </Box>
);
